import { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquareQuote, Send, Sparkles, Star, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import TurnstileWidget from '../components/TurnstileWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const TRUST_STAT_CONFIG = [
  {
    icon: Star,
    label: 'Average rating',
    valueKey: 'averageRating',
  },
  {
    icon: MessageSquareQuote,
    label: 'Testimonials shared',
    valueKey: 'testimonialCount',
  },
  {
    icon: ThumbsUp,
    label: 'Focus',
    valueKey: 'focusLabel',
  },
];

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    message: '',
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Please complete the captcha check');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/testimonials`, {
        ...formData,
        turnstileToken,
      });
      toast.success('Thank you for your testimonial!');
      setFormData({ name: '', rating: 5, message: '' });
      setTurnstileToken('');
      setTurnstileResetKey((current) => current + 1);
      if (response.data?.id) {
        setTestimonials((current) => [response.data, ...current]);
      } else {
        fetchTestimonials();
      }
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = testimonials.length
    ? (testimonials.reduce((sum, item) => sum + item.rating, 0) / testimonials.length).toFixed(1)
    : '5.0';

  const trustStatValues = {
    averageRating: `${averageRating}/5`,
    testimonialCount: `${testimonials.length}`,
    focusLabel: 'Trusted service',
  };

  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="testimonials-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Real feedback from clients across our service regions
            </div>
            <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-6xl dark:text-white">
              Testimonials that speak to
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                trust, clarity, and follow-through.
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              See what clients have shared about the experience of working with Connectify, from
              problem solving and installations to ongoing support.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {TRUST_STAT_CONFIG.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 p-3 text-white">
                    <stat.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-slate-950 dark:text-white">
                      {trustStatValues[stat.valueKey]}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-300">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="testimonials-list">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Client Stories
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                Experiences from people who trusted us with the job.
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
              Every testimonial here is pulled live from your current testimonials data, so this
              page grows naturally as new reviews come in.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
              <div className="h-14 w-14 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500 dark:border-white/10 dark:border-t-cyan-300" />
            </div>
          ) : testimonials.length > 0 ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  data-testid={`testimonial-${testimonial.id}`}
                  className="group flex h-full flex-col rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-8 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-current text-amber-400" />
                      ))}
                    </div>
                    <MessageSquareQuote className="h-5 w-5 text-sky-500/70 dark:text-cyan-300/70" />
                  </div>
                  <p className="flex-1 text-base italic leading-7 text-slate-700 dark:text-slate-200">
                    "{testimonial.message}"
                  </p>
                  <div className="mt-8 flex items-center gap-3 border-t border-slate-200/80 pt-5 dark:border-white/10">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-sm font-semibold text-white shadow-lg">
                      {testimonial.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-950 dark:text-white">{testimonial.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Verified client feedback</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-white/70 px-6 py-16 text-center text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              No testimonials yet. Be the first to share your experience.
            </div>
          )}
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="testimonial-form-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Share Your Experience
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              Help future clients feel confident about choosing Connectify.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-300">
              A short testimonial goes a long way. Tell people what we helped with, how the process
              felt, and what stood out most about the result.
            </p>

            <div className="mt-8 rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 p-3 text-slate-950">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Good testimonials feel specific</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    Mention the service you used, how communication went, or what problem was
                    solved. That helps new visitors trust what they are reading.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8 shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] backdrop-blur">
            <form onSubmit={handleSubmit} data-testid="testimonial-form" className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Your Name *</label>
                <Input
                  type="text"
                  data-testid="testimonial-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  required
                  className="w-full border-white/10 bg-white text-slate-900 placeholder:text-slate-400 dark:bg-white dark:text-slate-900"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-slate-200">Rating *</label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      data-testid={`rating-${rating}`}
                      onClick={() => setFormData({ ...formData, rating })}
                      className={`rounded-2xl border px-3 py-2 transition ${
                        rating <= formData.rating
                          ? 'border-amber-300 bg-amber-300/15'
                          : 'border-white/10 bg-white/5 hover:border-cyan-300/50'
                      }`}
                    >
                      <Star
                        className={`h-6 w-6 ${
                          rating <= formData.rating
                            ? 'fill-current text-amber-400'
                            : 'text-slate-500'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Your Review *</label>
                <Textarea
                  data-testid="testimonial-message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Share your experience with us..."
                  rows={6}
                  required
                  className="w-full border-white/10 bg-white text-slate-900 placeholder:text-slate-400 dark:bg-white dark:text-slate-900"
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-200">
                    Security Check *
                  </label>
                  <TurnstileWidget
                    siteKey={TURNSTILE_SITE_KEY}
                    onTokenChange={setTurnstileToken}
                    resetSignal={turnstileResetKey}
                    className="border-white/10 bg-white/5"
                  />
                  <p className="mt-2 text-xs text-slate-400">
                    This helps protect the testimonial form from spam and automated abuse.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                data-testid="submit-testimonial-btn"
                disabled={submitting}
                className="w-full bg-[#1E90FF] text-white transition duration-300 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span>Submit Testimonial</span>
                    <Send className="h-4 w-4" />
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
